import { gql } from "@apollo/client";
import { CRMNote, NoteNotable, NotePriority, NoteVisibility } from "@/types";
import { CRM_NOTE_FIELDS_FRAGMENT } from "@/fragments";

export const CREATE_NOTE_MUTATION = gql`
  mutation CreateNote($input: BackofficeCRMCreateNoteInput!) {
    createNote(input: $input) {
      code
      success
      message
      note {
        ...CRMNoteFields
      }
      task {
        id
      }
    }
  }
  ${CRM_NOTE_FIELDS_FRAGMENT}
`;

export const UPDATE_NOTE_MUTATION = gql`
  mutation UpdateNote($input: BackofficeCRMUpdateNoteInput!) {
    updateNote(input: $input) {
      code
      success
      message
      note {
        ...CRMNoteFields
      }
    }
  }
  ${CRM_NOTE_FIELDS_FRAGMENT}
`;

export const DELETE_NOTE_MUTATION = gql`
  mutation DeleteNote($noteId: ID!) {
    deleteNote(noteId: $noteId) {
      code
      success
      message
    }
  }
`;

import type { BackofficeCRMNestedTaskInput } from "@/hooks/useAttachTask";
export type { BackofficeCRMNestedTaskInput };

export type BackofficeCRMCreateNoteInput = {
  notableId: string;
  notableType: NoteNotable;
  title?: string | null;
  content: string;
  priority: NotePriority;
  visibility?: NoteVisibility;
  priorityExpirationDate?: string | null;
  task?: BackofficeCRMNestedTaskInput | null;
};

export type BackofficeCRMUpdateNoteInput = {
  id: string;
  title?: string | null;
  content?: string | null;
  priority?: NotePriority;
  priorityExpirationDate?: string | null;
};

export type BackofficeCRMNoteMutationResponse = {
  code: string;
  success: boolean;
  message: string;
  note: CRMNote | null;
  task?: { id: string } | null;
};

export type CreateNoteResponse = {
  createNote: BackofficeCRMNoteMutationResponse;
};

export type UpdateNoteResponse = {
  updateNote: BackofficeCRMNoteMutationResponse;
};

export type DeleteNoteResponse = {
  deleteNote: {
    code: string;
    success: boolean;
    message: string;
  };
};

const BULK_REFERENCE_RESPONSE_FIELDS = gql`
  fragment BulkReferenceResponseFields on BackofficeCRMBulkReferenceMutationResponse {
    code
    success
    message
    successfulReferences {
      referenceableId
      referenceableType
    }
    failedReferences {
      referenceableId
      referenceableType
    }
  }
`;

export const BULK_CREATE_REFERENCES_MUTATION = gql`
  mutation BulkCreateReferences($input: BackofficeCRMBulkReferencesInput!) {
    bulkCreateReferences(input: $input) {
      ...BulkReferenceResponseFields
    }
  }
  ${BULK_REFERENCE_RESPONSE_FIELDS}
`;

export const BULK_DELETE_REFERENCES_MUTATION = gql`
  mutation BulkDeleteReferences($input: BackofficeCRMBulkReferencesInput!) {
    bulkDeleteReferences(input: $input) {
      ...BulkReferenceResponseFields
    }
  }
  ${BULK_REFERENCE_RESPONSE_FIELDS}
`;

export type BackofficeCRMBulkReferencesInput = {
  referencedById: string;
  referencedByType: "NOTE" | "TASK";
  references: {
    referenceableId: string;
    referenceableType:
      | "PERSON_PROFILE_DOCUMENT"
      | "NOTE"
      | "INTERACTION"
      | "MEMBERSHIP_APPLICATION"
      | "ORDER"
      | "PAYMENT"
      | "PERSON"
      | "EVENT"
      | "FACILITY"
      | "CAREER_PLAN";
  }[];
};

export const TYPENAME_TO_REFERENCEABLE_TYPE: Record<
  string,
  BackofficeCRMBulkReferencesInput["references"][number]["referenceableType"]
> = {
  CRMNote: "NOTE",
  CRMInteraction: "INTERACTION",
  BackofficePersonProfileDocument: "PERSON_PROFILE_DOCUMENT",
  BackofficeMembershipApplication: "MEMBERSHIP_APPLICATION",
  CommerceOrder: "ORDER",
  CommercePayment: "PAYMENT",
  CRMPerson: "PERSON",
  CRMEvent: "EVENT",
  CRMTaskFacility: "FACILITY",
  BackofficeCareerPlan: "CAREER_PLAN"
};

export type CRMBulkReferenceable = {
  referenceableId: string;
  referenceableType: string;
};

export type BulkReferenceMutationResponse = {
  code: string;
  success: boolean;
  message: string | null;
  successfulReferences: CRMBulkReferenceable[] | null;
  failedReferences: CRMBulkReferenceable[] | null;
};

export type BulkCreateReferencesResponse = {
  bulkCreateReferences: BulkReferenceMutationResponse;
};

export type BulkDeleteReferencesResponse = {
  bulkDeleteReferences: BulkReferenceMutationResponse;
};
